"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useSearchContent } from "@/queries/search";
import { mapSearchMovieToContent, mapSearchSeriesToContent } from "./searchResultMapper";
import type { PersonContent } from "@/types/person";
import type { SearchPersonResult } from "@/types/search";

/**
 * Text-query search results (the "فیلم، سریال، بازیگر و ژانر" input), backed
 * by GET /search?q= — separate from useSearchCatalog, which still drives the
 * genre/country/year filters via /catalog. movies+series map into `items`
 * (same PersonContent[] shape useSearchCatalog already produces); persons
 * come back separately in `persons` for SearchResults to render as its own
 * row, since they don't fit the movie/series card shape.
 *
 * Same return shape as useSearchCatalog (items/isLoading/hasNextPage/...)
 * plus `persons`, so Search.tsx can swap between the two sources without
 * touching its JSX beyond passing the extra prop through.
 */
export function useSearchByQuery(q) {
  const $ = _c(13);
  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch
  } = useSearchContent(q);
  let t0;
  bb0: {
    const d = data?.data;
    if (!d) {
      let t1;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = [];
        $[0] = t1;
      } else {
        t1 = $[0];
      }
      t0 = t1;
      break bb0;
    }
    let t1;
    if ($[1] !== d.movies || $[2] !== d.series) {
      t1 = [...(d.movies ?? []).map(mapSearchMovieToContent), ...(d.series ?? []).map(mapSearchSeriesToContent)];
      $[1] = d.movies;
      $[2] = d.series;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    t0 = t1;
  }
  const items = t0;
  let t1;
  if ($[4] !== data?.data?.persons) {
    t1 = data?.data?.persons ?? [];
    $[4] = data?.data?.persons;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const persons = t1;
  const t2 = isFetching && !isLoading;
  let t3;
  if ($[6] !== isError || $[7] !== isLoading || $[8] !== items || $[9] !== persons || $[10] !== refetch || $[11] !== t2) {
    t3 = {
      items,
      persons,
      isLoading,
      isFetchingNextPage: t2,
      isError,
      refetch,
      hasNextPage: false,
      handleLoadMore: _temp
    };
    $[6] = isError;
    $[7] = isLoading;
    $[8] = items;
    $[9] = persons;
    $[10] = refetch;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
function _temp() {}